import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchGymCities,
  fetchGymCityLocation,
  fetchGymLandingPages,
  fetchMetabseLinks,
  fetchNearestGyms,
  fetchPartners,
  fetchTestimonial,
  fetchTraining,
  fetchWorkoutGoal,
  fetchGymAlias,
  fetchFranchiseLeads,
  fetchWhatsNew,
  fetchTrainerRatings,
  fetchTrainerRatingsStat,
} from "./websiteApis";
import { fetchCareer } from "../../data/APIs";

// What's New (ported from main) — read list of announcement cards
export const fetchWhatsNewData = createAsyncThunk(
  "webApi/fetchWhatsNewData",
  async (payload) => {
    const { token } = payload;
    const response = await fetchWhatsNew(token);
    return response;
  }
);

// Trainers Rating (ported from main) — read-only
export const fetchTrainerRatingsData = createAsyncThunk(
  "webApi/fetchTrainerRatingsData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchTrainerRatings(filters, token);
    return response;
  }
);

export const fetchTrainerRatingsStatData = createAsyncThunk(
  "webApi/fetchTrainerRatingsStat",
  async (payload, { getState }) => {
    // component dispatches the raw token string (not an object), so accept both
    const token = typeof payload === "string" ? payload : payload?.token;
    const response = await fetchTrainerRatingsStat(token);
    return response;
  }
);

export const fetchTestimonialData = createAsyncThunk(
  "api/fetchTestimonialData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchTestimonial(filters, token);
    return response;
  }
);

export const fetchWorkoutGoalData = createAsyncThunk(
  "api/fetchWorkoutGoalData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchWorkoutGoal(filters, token);
    return response;
  }
);

export const fetchPartnersData = createAsyncThunk(
  "api/fetchPartnersData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchPartners(filters, token);
    return response;
  }
);

export const fetchCareerData = createAsyncThunk(
  "api/fetchCareerData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchCareer(filters, token);
    return response;
  }
);

export const fetchGymCitiesData = createAsyncThunk(
  "webApi/fetchGymCitiesData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchGymCities(filters, token);
    return response;
  }
);

export const fetchGymCityLocationData = createAsyncThunk(
  "webApi/fetchGymCityLocationData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchGymCityLocation(filters, token);
    return response;
  }
);

export const fetchTrainingData = createAsyncThunk(
  "webApi/fetchTrainingData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchTraining(filters, token);
    return response;
  }
);

export const fetchMetabseLinksData = createAsyncThunk(
  "webApi/fetchMetabseLinksData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchMetabseLinks(filters, token);
    return response;
  }
);

export const fetchGymLandingPagesData = createAsyncThunk(
  "webApi/fetchGymLandingPagesData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchGymLandingPages(filters, token);
    return response;
  }
);

export const fetchNearestGymsData = createAsyncThunk(
  "webApi/fetchNearestGymsData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchNearestGyms(filters, token);
    return response;
  }
);

export const fetchGymAliasData = createAsyncThunk(
  "webApi/fetchGymAliasData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchGymAlias(filters, token);
    return response;
  }
);

export const fetchFranchiseLeadsData = createAsyncThunk(
  "webApi/fetchFranchiseLeadsData",
  async (payload, { getState }) => {
    const { filters, token } = payload;
    const response = await fetchFranchiseLeads(filters, token);
    return response;
  }
);

const websiteSlice = createSlice({
  name: "webApi",
  initialState: {
    testimonialData: null,
    workoutGoalData: null,
    partners: null,
    careerData: null,

    error: "",
    loading: false,

    gymCities: null,
    gymCitiesLoading: false,
    gymCitiesError: null,

    gymCityLocation: null,
    gymCityLocationLoading: false,
    gymCityLocationError: null,

    training: null,
    trainingLoading: false,
    trainingError: null,

    metabseLinks: null,
    metabseLinksLoading: false,
    metabseLinksError: null,

    gymLandingPages: null,
    gymLandingPagesLoading: false,
    gymLandingPagesError: null,

    nearestGyms: null,
    nearestGymsLoading: false,
    nearestGymsError: null,

    gymAlias: null,
    gymAliasLoading: false,
    gymAliasError: null,

    franchiseLeads: null,
    franchiseLeadsLoading: false,
    franchiseLeadsError: null,

    // Trainers Rating (ported from main)
    trainerRatings: null,
    trainerRatingsLoading: false,
    trainerRatingsError: null,

    trainerRatingsStat: null,
    trainerRatingsStatLoading: false,
    trainerRatingsStatError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // What's New (ported from main)
      .addCase(fetchWhatsNewData.pending, (state) => {
        state.whatsNewLoading = true;
        state.whatsNewError = null;
      })
      .addCase(fetchWhatsNewData.fulfilled, (state, action) => {
        state.whatsNewLoading = false;
        state.whatsNewData = action.payload;
      })
      .addCase(fetchWhatsNewData.rejected, (state, action) => {
        state.whatsNewLoading = false;
        state.whatsNewError = action.error.message;
      })

      // Trainers Rating (ported from main)
      .addCase(fetchTrainerRatingsData.pending, (state) => {
        state.trainerRatingsLoading = true;
        state.trainerRatingsError = null;
      })
      .addCase(fetchTrainerRatingsData.fulfilled, (state, action) => {
        state.trainerRatingsLoading = false;
        state.trainerRatings = action.payload;
      })
      .addCase(fetchTrainerRatingsData.rejected, (state, action) => {
        state.trainerRatingsLoading = false;
        state.trainerRatingsError = action.error.message;
      })
      .addCase(fetchTrainerRatingsStatData.pending, (state) => {
        state.trainerRatingsStatLoading = true;
      })
      .addCase(fetchTrainerRatingsStatData.fulfilled, (state, action) => {
        state.trainerRatingsStatLoading = false;
        state.trainerRatingsStat = action.payload;
      })
      .addCase(fetchTrainerRatingsStatData.rejected, (state, action) => {
        state.trainerRatingsStatLoading = false;
        state.trainerRatingsStatError = action.error.message;
      })
      .addCase(fetchTestimonialData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTestimonialData.fulfilled, (state, action) => {
        state.loading = false;
        state.testimonialData = action.payload;
      })
      .addCase(fetchTestimonialData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(fetchWorkoutGoalData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWorkoutGoalData.fulfilled, (state, action) => {
        state.loading = false;
        state.workoutGoalData = action.payload;
      })
      .addCase(fetchWorkoutGoalData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(fetchPartnersData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPartnersData.fulfilled, (state, action) => {
        state.loading = false;
        state.partners = action.payload;
      })
      .addCase(fetchPartnersData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(fetchCareerData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCareerData.fulfilled, (state, action) => {
        state.loading = false;
        state.careerData = action.payload;
      })
      .addCase(fetchCareerData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(fetchGymCitiesData.pending, (state) => {
        state.gymCitiesLoading = true;
      })
      .addCase(fetchGymCitiesData.fulfilled, (state, action) => {
        state.gymCitiesLoading = false;
        state.gymCities = action.payload;
      })
      .addCase(fetchGymCitiesData.rejected, (state, action) => {
        state.gymCitiesLoading = false;
        state.gymCitiesError = action.error.message;
      })

      .addCase(fetchGymCityLocationData.pending, (state) => {
        state.gymCityLocationLoading = true;
      })
      .addCase(fetchGymCityLocationData.fulfilled, (state, action) => {
        state.gymCityLocationLoading = false;
        state.gymCityLocation = action.payload;
      })
      .addCase(fetchGymCityLocationData.rejected, (state, action) => {
        state.gymCityLocationLoading = false;
        state.gymCityLocationError = action.error.message;
      })

      .addCase(fetchTrainingData.pending, (state) => {
        state.trainingLoading = true;
      })
      .addCase(fetchTrainingData.fulfilled, (state, action) => {
        state.trainingLoading = false;
        state.training = action.payload;
      })
      .addCase(fetchTrainingData.rejected, (state, action) => {
        state.trainingLoading = false;
        state.trainingError = action.error.message;
      })

      .addCase(fetchMetabseLinksData.pending, (state) => {
        state.metabseLinksLoading = true;
      })
      .addCase(fetchMetabseLinksData.fulfilled, (state, action) => {
        state.metabseLinksLoading = false;
        state.metabseLinks = action.payload;
      })
      .addCase(fetchMetabseLinksData.rejected, (state, action) => {
        state.metabseLinksLoading = false;
        state.metabseLinksError = action.error.message;
      })

      .addCase(fetchGymLandingPagesData.pending, (state) => {
        state.gymLandingPagesLoading = true;
      })
      .addCase(fetchGymLandingPagesData.fulfilled, (state, action) => {
        state.gymLandingPagesLoading = false;
        state.gymLandingPages = action.payload;
      })
      .addCase(fetchGymLandingPagesData.rejected, (state, action) => {
        state.gymLandingPagesLoading = false;
        state.gymLandingPagesError = action.error.message;
      })

      .addCase(fetchNearestGymsData.pending, (state) => {
        state.nearestGymsLoading = true;
      })
      .addCase(fetchNearestGymsData.fulfilled, (state, action) => {
        state.nearestGymsLoading = false;
        state.nearestGyms = action.payload;
      })
      .addCase(fetchNearestGymsData.rejected, (state, action) => {
        state.nearestGymsLoading = false;
        state.nearestGymsError = action.error.message;
      })

      .addCase(fetchGymAliasData.pending, (state) => {
        state.gymAliasLoading = true;
      })
      .addCase(fetchGymAliasData.fulfilled, (state, action) => {
        state.gymAliasLoading = false;
        state.gymAlias = action.payload;
      })
      .addCase(fetchGymAliasData.rejected, (state, action) => {
        state.gymAliasLoading = false;
        state.gymAliasError = action.error.message;
      })

      .addCase(fetchFranchiseLeadsData.pending, (state) => {
        state.franchiseLeadsLoading = true;
      })
      .addCase(fetchFranchiseLeadsData.fulfilled, (state, action) => {
        state.franchiseLeadsLoading = false;
        state.franchiseLeads = action.payload;
      })
      .addCase(fetchFranchiseLeadsData.rejected, (state, action) => {
        state.franchiseLeadsLoading = false;
        state.franchiseLeadsError = action.error.message;
      });
  },
});

export const { clearGymAliasMessages } = websiteSlice.actions;
export default websiteSlice.reducer;
